/**
 * CR-6450: supplier-view layout for the Sales Orders → Deliveries filter panel.
 *
 * Renders exactly the controls in the supplied design, grouped into its four
 * sections — Date Range · Company · Supplier · Order Details — with the same
 * section shell (`FilterSection` + divider) as the dashboard and the other
 * supplier-view panels, and this panel's control density
 * (`deliveryFilterStyles`). Every section uses the shared four-column grid
 * (`gridCls`), as the customer layout does: the first pass sized Date Range
 * and Company two-up and Supplier three-up, which stretched those controls
 * across the panel and made the two views read differently (QA, 2026-09-22).
 *
 * Every control keeps its input type and handler; only the grouping, labels
 * and the field set changed. Rework round 1 (QA, 2026-08-07) asked for three
 * things, all here:
 *  - Supplier Country added. It narrows Supplier Site (the parent's existing
 *    `supplierCountry` cascade), the same way it does on the customer layout.
 *  - Commodity Type removed — not in the design. Commodity Group and Supplier
 *    Code went with it for the same reason; all three stay on the customer
 *    layout, where the design lists them.
 *  - Sections drawn with the shared primitive so they read like the dashboard.
 *
 * The design draws "GRN status" and "On-time status" as two boxes. Only one
 * on-time filter exists — it reads the GRN's on_time_status (On Time / Early /
 * Late / Early/Late) — and it ships once, labelled On-Time Status: the name the
 * customer layout uses for the same control, the name this view carried before
 * the rework, and the field it actually filters. A GRN carries other statuses
 * (inspection, workflow) but neither is a supported Deliveries filter, so no
 * separate "GRN status" box is offered.
 */
import { Calendar, Building2, Truck, FileText } from 'lucide-react';
import { ORIGINAL_REQUIRED_DATE } from '../../../../config/dateLabels';
import SearchableSelect from '../../../ui/SearchableSelect';
import { FilterSection } from '../../../filters/FilterSection';
import { countryDisplayNames } from '../../../dashboard/dashboardFilterUtils';
import {
  inputCls, labelCls, dateWrap, dateInputCls, gridCls,
} from './deliveryFilterStyles';
import { useFilterVisibility } from '../../../../hooks/useFilterVisibility';

/** Horizontal divider with padding so it doesn't touch card edges */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

interface FilterOption {
  value: string;
  label: string;
  /** On supplier options: drives Supplier Country → Supplier Site. */
  country?: string;
  group?: number | null;
}

export interface DeliveryFilterSupplierLayoutProps {
  filters: Record<string, string>;
  textInputs: Record<string, string>;
  pendingDates: { dateFrom: string; dateTo: string; oddFrom: string; oddTo: string };
  quickRanges: ReadonlyArray<{ key: string; label: string }>;
  getQuickRangeDates: (key: string) => { dateFrom: string; dateTo: string };
  viewScopedSites: FilterOption[];
  warehouseOptions: Array<{ id: number; name: string }>;
  supplierGroupOptions: FilterOption[];
  supplierOptions: FilterOption[];
  /** Country values present on the suppliers in scope. */
  supplierCountryOptions?: string[];
  onFilterChange: (key: string, value: string) => void;
}

export function DeliveryFilterSupplierLayout({
  filters,
  textInputs,
  pendingDates,
  quickRanges,
  getQuickRangeDates,
  viewScopedSites,
  warehouseOptions,
  supplierGroupOptions,
  supplierOptions,
  supplierCountryOptions = [],
  onFilterChange,
}: DeliveryFilterSupplierLayoutProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  // Supplier Country narrows Supplier Site so the panel cannot express a
  // combination that is empty by construction. Clearing a now-invalid site
  // selection happens where the filter state lives (DeliveryTab).
  const suppliersForSelection = filters['supplierCountry']
    ? supplierOptions.filter((s) => s.country === filters['supplierCountry'])
    : supplierOptions;

  const dateField = (label: string, key: 'dateFrom' | 'dateTo' | 'oddFrom' | 'oddTo') => (
    <div>
      <label className={labelCls}>{label}</label>
      <div className={dateWrap}>
        <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          type="date"
          value={pendingDates[key]}
          onChange={(e) => onFilterChange(key, e.target.value)}
          className={dateInputCls}
        />
      </div>
    </div>
  );

  const textField = (label: string, key: string, value: string, placeholder: string) => (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onFilterChange(key, e.target.value)}
        className={inputCls}
      />
    </div>
  );

  const selectField = (
    label: string,
    key: string,
    options: ReadonlyArray<{ value: string; label: string }>,
    allLabel: string,
  ) => (
    <div>
      <label className={labelCls}>{label}</label>
      <select
        value={filters[key] || ''}
        onChange={(e) => onFilterChange(key, e.target.value)}
        className={inputCls}
      >
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );

  const searchableField = (label: string, key: string, options: FilterOption[], placeholder: string) => (
    <div>
      <label className={labelCls}>{label}</label>
      <SearchableSelect
        options={options}
        value={filters[key] || ''}
        onChange={(val) => onFilterChange(key, val)}
        placeholder={placeholder}
      />
    </div>
  );

  // CR-7106: one quick-range row per date pair — the same ranges apply to the
  // ODD pair, which previously offered only raw date inputs.
  const quickRangeRow = (fromKey: 'dateFrom' | 'oddFrom', toKey: 'dateTo' | 'oddTo', rowLabel: string) => (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <span className="text-xs font-medium text-muted-foreground w-48">{rowLabel}</span>
      {quickRanges.map(({ key, label }) => {
        const dates = getQuickRangeDates(key);
        // CR-6364: reflect the pending (not-yet-applied) selection so a
        // clicked range highlights before it is applied.
        const isActive = pendingDates[fromKey] === dates.dateFrom && pendingDates[toKey] === dates.dateTo;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={isActive}
            onClick={() => {
              onFilterChange(fromKey, dates.dateFrom);
              onFilterChange(toKey, dates.dateTo);
            }}
            className={`px-3 py-1.5 text-xs font-medium border rounded-md transition-colors ${
              isActive
                ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="[&>section]:border-t-0">
      {/* Divider above first section */}
      <SectionDivider />

      {/* Date Range — quick ranges, then the delivery-date pair and the ODD pair */}
      <FilterSection title="Date Range" icon={Calendar}>
        {quickRangeRow('dateFrom', 'dateTo', 'Delivery Date:')}
        {quickRangeRow('oddFrom', 'oddTo', `${ORIGINAL_REQUIRED_DATE}:`)}
        <div className={gridCls}>
          {dateField('Delivery Date From', 'dateFrom')}
          {dateField('Delivery Date To', 'dateTo')}
          {dateField(`${ORIGINAL_REQUIRED_DATE} From`, 'oddFrom')}
          {dateField(`${ORIGINAL_REQUIRED_DATE} To`, 'oddTo')}
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Company */}
      <FilterSection title="Company" icon={Building2}>
        <div className={gridCls}>
          {searchableField('Site', 'site', viewScopedSites, 'All Sites')}
          {fv.warehouse && selectField(
            'Warehouse',
            'warehouse',
            warehouseOptions.map((wh) => ({ value: String(wh.id), label: wh.name })),
            'All Warehouses',
          )}
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Supplier — Country and Group both narrow Site (Group through the
          parent's existing onFilterChange cascade; nothing about it moves). */}
      <FilterSection title="Supplier" icon={Truck}>
        <div className={gridCls}>
          {selectField(
            'Country',
            'supplierCountry',
            supplierCountryOptions.map((c) => ({ value: c, label: countryDisplayNames[c] || c })),
            'All Countries',
          )}
          {searchableField('Group', 'supplierGroup', supplierGroupOptions, 'All Supplier Groups')}
          {searchableField('Site', 'supplier', suppliersForSelection, 'All Supplier Sites')}
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Order Details */}
      <FilterSection title="Order Details" icon={FileText}>
        <div className={gridCls}>
          {textField('PO Number', 'poNumber', textInputs['poNumber'] || '', 'PO Number...')}
          {selectField('PO Line Status', 'poLineStatus', [
            { value: 'open', label: 'Open' },
            { value: 'closed', label: 'Closed' },
          ], 'All')}
          {textField('GRN Number', 'grnNumber', textInputs['grnNumber'] || '', 'GRN Number...')}
          {selectField('On-Time Status', 'grnStatus', [
            { value: 'on_time', label: 'On Time' },
            { value: 'early', label: 'Early' },
            { value: 'late', label: 'Late' },
            { value: 'early_late', label: 'Early/Late' },
          ], 'All')}
          {textField('Line Number', 'lineNumber', textInputs['lineNumber'] || '', 'Line number...')}
          {textField('Part Number', 'partNumber', textInputs['partNumber'] || '', 'Part number...')}
          {selectField('Appeal Status', 'appealFilter', [
            { value: 'no_appeal', label: 'Not Appealed' },
            { value: 'appealed', label: 'Appealed' },
          ], 'All')}
        </div>
      </FilterSection>
    </div>
  );
}
